import { supabase } from './supabase'

export async function getCustomerByUserId(userId) {
  if (!supabase) return null
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data || null
}

export async function createCustomer({ userId, email, name }) {
  if (!supabase) return null
  const { data, error } = await supabase
    .from('customers')
    .insert({ user_id: userId, email, name })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function getOrCreateCustomer({ user }) {
  if (!user) return null
  let cust = await getCustomerByUserId(user.id)
  if (!cust) {
    cust = await createCustomer({ userId: user.id, email: user.email, name: user.user_metadata?.name || null })
  }
  return cust
}

export function getDeviceId() {
  if (typeof window === 'undefined') return ''
  const KEY = 'opraiz_device_id'
  let id = window.localStorage.getItem(KEY)
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`
    window.localStorage.setItem(KEY, id)
  }
  return id
}

export function getDeviceLabel() {
  if (typeof navigator === 'undefined') return 'Unknown device'
  const ua = navigator.userAgent || ''
  let os = 'Unknown OS'
  if (/Windows/i.test(ua)) os = 'Windows'
  else if (/Mac OS X/i.test(ua)) os = 'macOS'
  else if (/Android/i.test(ua)) os = 'Android'
  else if (/iPhone|iPad/i.test(ua)) os = 'iOS'
  else if (/Linux/i.test(ua)) os = 'Linux'

  let browser = 'Browser'
  if (/Edg\//i.test(ua)) browser = 'Edge'
  else if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome'
  else if (/Firefox/i.test(ua)) browser = 'Firefox'
  else if (/Safari/i.test(ua)) browser = 'Safari'
  else if (/Chromium/i.test(ua)) browser = 'Chromium'

  return `${browser} on ${os}`
}

export async function recordSession(userId) {
  if (!supabase || !userId) return
  const deviceId = getDeviceId()
  const { error } = await supabase.from('user_sessions').upsert(
    {
      user_id: userId,
      device_id: deviceId,
      device_label: getDeviceLabel(),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 500) : null,
      last_active_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,device_id' }
  )
  if (error && !/already exists|duplicate/i.test(error.message || '')) {
    // Ignore transient failures (e.g. table not deployed); never break auth flow.
  }
}

// Numbers that should never be OTP'd — well-known illustrative/test numbers
// (ITU-T E.164 test ranges + commonly-abused placeholders). E164_RELEASE,
// 555-0100 (US fictional range), and repeated/zero placeholders. Excluded so no
// third-party or placebo mobile ever receives a genuine OTP.
const BLOCKED_MOBILE = new Set([
  '9876543210',
  '987654321',
  '0000000000',
  '1234567890',
  '1111111111',
  '9888888888',
  '2222222222',
  '3333333333',
  '4444444444',
  '5555555555',
  '6666666666',
  '7777777777',
  '8888888888',
  '9999999999',
  // ITU-T E.164 test number ranges (reserved, never a real subscriber)
  '15105550100',
  '15105550101',
  '15105550199',
])

// Normalize a phone to an E.164-style digit string. Accepts international
// (+CC...) or national formats; leading 0 is a national trunk prefix and is
// dropped only when no + country code is present.
export function normalizeMobile(phone) {
  const normalized = String(phone || '').replace(/[\s()-]/g, '')
  if (!normalized) return ''
  const isInternational = normalized.startsWith('+')
  let digits = normalized.replace(/\D/g, '')
  if (!isInternational) digits = digits.replace(/^0+/, '')
  return digits
}

// True for valid 7–14 digit (E.164) numbers, excluding reserved test numbers.
export function validateMobile(phone) {
  const digits = normalizeMobile(phone)
  if (!/^[1-9]\d{6,13}$/.test(digits)) return false
  // A number isn't allowed to be all-identical digits or a plain placeholder run.
  if (BLOCKED_MOBILE.has(digits)) return false
  // Block the E.164 test range by looking at the trailing local part too, so a
  // user typing '555-0100'-style US tests is caught regardless of leading code.
  const local7 = digits.slice(-10)
  return !/^(000|111|222|333|444|555|666|777|888|999)\d{7}$/.test(local7) &&
    !/^1?51?0550100(1?|99)$/.test(digits)
}

export async function sendPhoneOtp(phone) {
  if (!supabase) {
    console.warn('[profile] sendPhoneOtp called with no backend configured')
    throw new Error('Phone verification is not available right now. Please try again in a moment.')
  }
  const { error } = await supabase.functions.invoke('send-otp', {
    body: { phone },
  })
  if (error) throw error
}

export async function verifyPhoneOtp(phone, otp) {
  if (!supabase) {
    console.warn('[profile] verifyPhoneOtp called with no backend configured')
    throw new Error('Phone verification is not available right now. Please try again in a moment.')
  }
  const { data, error } = await supabase.functions.invoke('verify-otp', {
    body: { phone, otp },
  })
  if (error) throw error
  return data
}
